/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { defineMessages } from 'react-intl';

export const messages = defineMessages({
  title: {
    id: 'channels.modals.title',
    defaultMessage: 'Edit channel'
  },
  editBody: {
    id: 'channels.modals.editBody',
    defaultMessage:
      'You are about to be directed to your {integrationName} integration. Please select the environment for your {integrationName}. Edits to your integration will only apply for that environment.'
  },
  editOpenTitle: {
    id: 'channels.modals.editOpenTitle',
    defaultMessage: 'Open {integrationName}'
  },
  environment: {
    id: 'channels.modals.environment',
    defaultMessage: 'Environment'
  },
  chooseEnvironment: {
    id: 'channels.modals.chooseEnvironment',
    defaultMessage: 'Choose the environment to edit'
  },
  createModalTitle: {
    id: 'channels.modals.createModalTitle',
    defaultMessage: 'Create {name} integrations'
  },
  createModalBody: {
    id: 'channels.modals.createModalBody',
    defaultMessage: 'You are about to create a {name} integration.'
  },
  webChatDescription: {
    id: 'channels.modals.create.webChatDescription',
    defaultMessage:
      'Embed the web chat onto your company website so it can answer questions.'
  },
  supportedContactCenterIntegrations: {
    id: 'channels.modals.create.supportedContactCenterIntegrations',
    defaultMessage: 'Contact center integrations'
  },
  phoneDescription: {
    id: 'channels.modals.create.phoneDescription',
    defaultMessage:
      'Give your system a voice so it can help customers over the phone.'
  },
  searchDescription: {
    id: 'channels.modals.create.searchDescription',
    defaultMessage:
      'Extend the scope of what your assistant can answer by searching documents and websites.'
  },
  smsDescription: {
    id: 'channels.modals.create.smsDescription',
    defaultMessage: 'Add SMS capability to your voice assistant on Phone.'
  },
  builtByIBM: {
    id: 'channels.modals.create.builtByIBM',
    defaultMessage: 'Built by IBM'
  },
  builtByDiscovery: {
    id: 'channels.modals.create.builtByIBM',
    defaultMessage: 'Powered by IBM Watson Discovery'
  },
  poweredBySegment: {
    id: 'channels.modals.create.poweredBySegment',
    defaultMessage: 'Powered by Segment'
  },
  segmentIntegration: {
    id: 'channels.modals.create.segmentIntegration',
    defaultMessage: 'Segment Integration'
  },
  segmentDescription: {
    id: 'channels.modals.create.segmentDescription',
    defaultMessage:
      "Better understand your users' end-to-end journey by combining assistant data with other sources."
  },
  segmentCreateModal: {
    id: 'channels.modals.create.segmentCreateModal',
    defaultMessage:
      'Get a more complete picture of your users by connecting IBM {productName} to Segment. Integrating with Segment allows data from your assistant to be combined with data from other sources and tools, giving you a more detailed look at where your assistant fits in your users’ end-to-end journeys.'
  },
  segmentLearnMore: {
    id: 'channels.modals.create.segmentLearnMore',
    defaultMessage: 'Learn more about '
  },
  segmentLearnMoreLink: {
    id: 'channels.modals.create.segmentLearnMoreLink',
    defaultMessage: 'how Segment works.'
  },
  webChatCreateModal1: {
    id: 'channels.modals.create.webChatCreateModal1',
    defaultMessage:
      'Add your assistant to your company website as a web chat widget that can help your customers with common questions and tasks. You can configure how and where the web chat widget appears, and you can use theming to align it with your branding and website design.'
  },
  webChatCreateModal2: {
    id: 'channels.modals.create.webChatCreateModal2',
    defaultMessage:
      'If a customer needs help from a person, the web chat integration can transfer the conversation to an agent.'
  },
  whatsAppCreateModal: {
    id: 'channels.modals.create.whatsAppCreateModal',
    defaultMessage:
      'Integrate with WhatsApp so your assistant can exchange messages with your customers using the popular messenger, available on phones all over the world.'
  },
  slackCreateModal: {
    id: 'channels.modals.create.slackCreateModal',
    defaultMessage:
      'Let your assistant respond directly in Slack. Add your assistant as a bot user to your Slack app, answering questions from direct messages or channels.'
  },
  genesysBotConnectorCreateModal: {
    id: 'channels.modals.create.genesysBotConnectorCreateModal',
    defaultMessage:
      'Let your assistant connect with other chatbot platforms using Genesys bot connector.'
  },
  microsoftTeamsCreateModal: {
    id: 'channels.modals.create.microsoftTeamsCreateModal',
    defaultMessage:
      'Let your assistant respond directly through Microsoft Teams. Add your assistant as a bot user to your Microsoft Teams app, answering questions from direct messages or channels.'
  },
  fbCreateModal: {
    id: 'channels.modals.create.fbCreateModal',
    defaultMessage:
      'Have your assistant respond directly in Facebook Messenger. Integrate your assistant to answer questions from your Facebook page and the Messenger app in browsers or mobile apps.'
  },
  smsCreateModal: {
    id: 'channels.modals.create.smsCreateModal',
    defaultMessage:
      'Give your assistant the ability to send and receive text messages with users. The integration provides Short Messaging Service (SMS) 160 character text-only messages or Multimedia Messaging Service (MMS) longer messages with images and text.'
  },
  intercomCreateModal: {
    id: 'channels.modals.create.intercomCreateModal',
    defaultMessage:
      'Let your assistant respond directly in Intercom. Add your assistant as a bot user to your Intercom app.'
  },
  searchCreateModal: {
    id: 'channels.modals.create.searchCreateModal',
    defaultMessage:
      "Embed your existing help content by integrating your assistant with search via IBM Watson Discovery. This gives your assistant access to your organization's data collections that it can mine for answers. Customer questions are used as search queries to find relevant answers for your users."
  },
  searchCreateModalBringYourOwn: {
    id: 'channels.modals.create.searchCreateModalBringYourOwn',
    defaultMessage:
      '{productName} also lets you Bring Your Own Search, so you can leverage your site search, knowledge base search, or content management system search that you have already invested in. Whether you are using IBM Watson Discovery or another tool, Bring Your Own Search also gives you full control over how search results are shown to the end user, and lets you enable advanced filtering so users only see results personalized for them and their issue. To set up Bring Your Own Search, '
  },
  searchCreateModalBringYourOwnSeeHere: {
    id: 'channels.modals.create.searchCreateModalBringYourOwnSeeHere',
    defaultMessage: 'See here'
  },
  phoneCreateModal1: {
    id: 'channels.modals.create.phoneCreateModal1',
    defaultMessage:
      'This integration enables your assistant to converse with end users using IBM Watson speech services. If an end user asks to speak to a person, the phone integration can transfer the call to an agent.'
  },
  phoneCreateModal2: {
    id: 'channels.modals.create.phoneCreateModal2',
    defaultMessage:
      'If a customer needs help from a person, this is the virtual line that connects your assistant with a phone number.'
  },
  supportedSip: {
    id: 'channels.modals.create.supportedSip',
    defaultMessage: 'Supported live agent integrations'
  },
  supportedProviders: {
    id: 'channels.modals.create.supportedProviders',
    defaultMessage: 'Supported SMS Providers'
  },
  // Reset modal
  resetModalHeading: {
    id: 'channels.modals.reset.resetModalHeading',
    defaultMessage: 'Are you sure you want to delete this connection?'
  },
  resetModalBody: {
    id: 'channels.modals.reset.resetModalBody',
    defaultMessage:
      'To change the connection, you must delete {serviceDeskName}. The configuration will be lost, and this action cannot be undone.'
  },
  cannotResetSubtitle: {
    id: 'channels.modals.reset.cannotResetSubtitle',
    defaultMessage:
      'You cannot delete the service desk because of insufficient permissions'
  },
  // Shared modal strings
  delete: {
    id: 'channels.modals.delete',
    defaultMessage: 'Delete'
  },
  remove: {
    id: 'channels.modals.remove',
    defaultMessage: 'Remove'
  },
  cancel: {
    id: 'channels.modals.cancel',
    defaultMessage: 'Cancel'
  },
  confirm: {
    id: 'channels.modals.confirm',
    defaultMessage: 'Confirm'
  },
  add: {
    id: 'channels.modals.add',
    defaultMessage: 'Add'
  },
  deleteInputPlaceholderText: {
    id: 'channels.modals.deleteInputPlaceholderText',
    defaultMessage: 'Enter text'
  },
  // Delete modal
  deleteModalHeading: {
    id: 'channels.modals.delete.deleteModalHeading',
    defaultMessage: 'Are you sure you want to delete this channel?'
  },
  removeExtensionModalHeading: {
    id: 'channels.modals.remove.removeExtensionModalHeading',
    defaultMessage:
      'Are you sure you want to remove {catalogItemName} from your assistant?'
  },
  deleteModalBody: {
    id: 'channels.modals.delete.deleteModalBody',
    defaultMessage:
      'You are about to delete {integrationName} from all the environments. The configuration will be lost and the action cannot be undone.'
  },
  removeExtensionModalBody1: {
    id: 'channels.modals.remove.removeExtensionModalBody1',
    defaultMessage:
      'You’re about to remove this custom extension: {catalogItemName}'
  },
  removeExtensionModalBody2: {
    id: 'channels.modals.remove.removeExtensionModalBody2',
    defaultMessage:
      'The extension will be removed from all the environments. The configuration will be lost and the action cannot be undone.'
  },
  deleteInputLabelText: {
    id: 'channels.modals.delete.deleteInputLabelText',
    defaultMessage: 'Please type DELETE to confirm this action.'
  },
  removeInputLabelText: {
    id: 'channels.modals.remove.removeInputLabelText',
    defaultMessage: 'Please type REMOVE to confirm this action.'
  },
  // Add catalog item modal
  customExtensionModalLabel: {
    id: 'channels.modals.add.customExtensionModalLabel',
    defaultMessage: 'Custom extension'
  },
  // Delete catalog item modal
  deleteCatalogItemModalHeading: {
    id: 'channels.modals.delete.deleteModalHeading',
    defaultMessage:
      'Are you sure you want to permanently delete {catalogItemName}?'
  },
  deleteModalBody2: {
    id: 'channels.modals.delete.deleteModalBody2',
    defaultMessage:
      'The extension will be lost and won’t be available in the integrations catalog. This removal can’t be undone.'
  },
  //segment upgrade modal
  bookDemo: {
    id: 'channels.modals.segmentUpgrade.bookDemo',
    defaultMessage: 'Book a demo'
  }
});
